import { createContext } from "react";
import type { HttpClient } from "../api/HttpClient";
import type { Credentials, EmailConfirmationHandle, EventEmitter, RegisterCredentials, User } from "../types";

export interface AuthContextValue {
  /** null while loading, or once logged out. */
  user: User | null;
  /** True until the initial session restore (from storage / redirect callback) completes. */
  loading: boolean;
  authenticated: boolean;
  login: (credentials: Credentials) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Create an account and start a session, same as `login()`. Only present
   * when the configured provider implements `RegistrableAuthProvider` (e.g.
   * `JwtProvider`) — undefined for redirect-flow providers.
   */
  register?: (credentials: RegisterCredentials) => Promise<void>;
  /** Ask the backend to email a confirmation code. Requires an active session. Same availability as `register`. */
  requestEmailConfirmation?: (email: string) => Promise<EmailConfirmationHandle>;
  /** Redeem a code against the `nonce` from `requestEmailConfirmation`. Same availability as `register`. */
  confirmEmailConfirmation?: (nonce: string, code: string) => Promise<void>;
  /** Use `auth.client.request(...)` instead of raw `fetch()` for authenticated calls. */
  client: HttpClient;
  events: EventEmitter;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
